import { useMemo, useState } from 'react';
import { Button, Chip, Switch } from '@nyce/admin-kit/ui';
import { db, type DbServerRef, type DbSession } from '../../api/db';
import { EmptyState, ErrorBox, Loading, TableScroller, useAsync } from '../../components/common';
import { formatDuration } from '../../lib/format';
import { ConfirmDialog, DatabasePicker, useInterval, useServerDatabases, type DbScopes } from './shared';

type Action = { kind: 'cancel' | 'terminate'; session: DbSession } | null;

const STATE_CLASS: Record<string, string> = {
  active: 'dbx-st-running',
  idle: 'dbx-st-off',
  'idle in transaction': 'dbx-st-queued',
  'idle in transaction (aborted)': 'dbx-st-failed',
};

export function SessionsTab({
  server,
  database,
  onDatabase,
  scopes,
  notify,
}: {
  server: DbServerRef;
  database: string;
  onDatabase: (db: string) => void;
  scopes: DbScopes;
  notify: (text: string) => void;
}) {
  const dbs = useServerDatabases(server.id);
  const list = useAsync(() => db.sessions(server.id, database || undefined), [server.id, database]);
  const [auto, setAuto] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [focus, setFocus] = useState<number | null>(null);
  const [action, setAction] = useState<Action>(null);
  useInterval(() => void list.reload(), 5000, auto);

  const rows = useMemo(() => list.data ?? [], [list.data]);
  /** pids some other session waits on. */
  const blockers = useMemo(() => new Set(rows.flatMap((r) => r.blockedBy)), [rows]);

  const toggle = (pid: number) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  const jump = (pid: number) => {
    setFocus(pid);
    document.querySelector(`[data-pid="${pid}"]`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  };
  const run = async (a: NonNullable<Action>) => {
    const call = a.kind === 'cancel' ? db.cancelSession : db.terminateSession;
    const r = await call(server.id, a.session.pid);
    setAction(null);
    notify(
      r.ok
        ? `${a.kind === 'cancel' ? 'Cancelled the query of' : 'Terminated'} session ${a.session.pid}`
        : `Session ${a.session.pid} had already ended`
    );
    void list.reload();
  };

  return (
    <>
      <div className="dbx-toolbar">
        <DatabasePicker databases={dbs.names} value={database} onChange={onDatabase} all="All databases" />
        <Switch checked={auto} onChange={setAuto} label="Auto-refresh 5 s" />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void list.reload()}
          loading={list.loading && !!list.data}
        >
          Refresh
        </Button>
        <span className="dbx-muted">{list.data ? `${rows.length} sessions` : ''}</span>
      </div>
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !rows.length && <EmptyState card title="No client sessions" />}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards dbx-sessions" data-testid="db-sessions">
              <thead>
                <tr>
                  <th className="nyk-ui-num">PID</th>
                  <th>Database</th>
                  <th>User</th>
                  <th>Application</th>
                  <th>Client</th>
                  <th>State</th>
                  <th>Wait</th>
                  <th className="nyk-ui-num">Duration</th>
                  <th>Query</th>
                  {scopes.copy && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const blocked = s.blockedBy.length > 0;
                  const open = expanded.has(s.pid);
                  return (
                    <tr
                      key={s.pid}
                      data-pid={s.pid}
                      className={[
                        blocked ? 'dbx-blocked' : '',
                        blockers.has(s.pid) ? 'dbx-blocker' : '',
                        focus === s.pid ? 'dbx-focus' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <td className="nyk-ui-num" data-label="PID">
                        {s.pid}
                        {blockers.has(s.pid) && (
                          <div>
                            <Chip size="sm" className="dbx-st-failed">
                              blocking
                            </Chip>
                          </div>
                        )}
                      </td>
                      <td data-label="Database">{s.database ?? '—'}</td>
                      <td data-label="User">{s.user ?? '—'}</td>
                      <td className="dbx-clip" data-label="Application" title={s.application ?? ''}>
                        {s.application || '—'}
                      </td>
                      <td data-label="Client">{s.clientAddr ?? 'local'}</td>
                      <td data-label="State">
                        {s.state ? (
                          <Chip size="sm" className={`dbx-status ${STATE_CLASS[s.state] ?? ''}`}>
                            {s.state}
                          </Chip>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td data-label="Wait">
                        {s.waitEventType ? (
                          <span title={s.waitEvent ?? ''}>
                            {s.waitEventType}
                            {s.waitEvent ? <span className="dbx-muted"> · {s.waitEvent}</span> : null}
                          </span>
                        ) : (
                          '—'
                        )}
                        {blocked && (
                          <div className="dbx-blocked-by">
                            blocked by{' '}
                            {s.blockedBy.map((p, i) => (
                              <span key={p}>
                                {i > 0 && ', '}
                                <button type="button" className="nyk-link" onClick={() => jump(p)}>
                                  {p}
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="nyk-ui-num" data-label="Duration">
                        {formatDuration(s.durationMs)}
                      </td>
                      <td className="dbx-query-cell" data-label="Query">
                        {s.query ? (
                          <button
                            type="button"
                            className={`dbx-query${open ? ' dbx-query-open' : ''}`}
                            aria-expanded={open}
                            title={open ? 'Collapse' : 'Show the whole query'}
                            onClick={() => toggle(s.pid)}
                          >
                            {s.query}
                          </button>
                        ) : (
                          <span className="dbx-muted">—</span>
                        )}
                      </td>
                      {scopes.copy && (
                        <td className="nyk-row-actions">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setAction({ kind: 'cancel', session: s })}
                            disabled={s.state !== 'active'}
                            title="Cancel the running query (pg_cancel_backend)"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setAction({ kind: 'terminate', session: s })}
                            title="Close the connection (pg_terminate_backend)"
                          >
                            Terminate
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {action && (
        <ConfirmDialog
          title={
            action.kind === 'cancel'
              ? `Cancel query of ${action.session.pid}`
              : `Terminate ${action.session.pid}`
          }
          confirmLabel={action.kind === 'cancel' ? 'Cancel query' : 'Terminate session'}
          danger
          onClose={() => setAction(null)}
          onConfirm={() => run(action)}
        >
          <p>
            {action.kind === 'cancel'
              ? 'Cancels the statement this session is running; the connection stays open and its transaction rolls back.'
              : 'Closes the connection; its open transaction rolls back and the client sees a dropped connection.'}
          </p>
          <p className="dbx-muted">
            {[
              action.session.user,
              action.session.database,
              action.session.application,
              action.session.clientAddr,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <pre className="dbx-sql" data-testid="confirm-query">
            {action.session.query || '(no query)'}
          </pre>
        </ConfirmDialog>
      )}
    </>
  );
}
